import { Component, OnInit, signal, ChangeDetectionStrategy, ChangeDetectorRef, inject } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { AgentsService } from '@generated/services/agents.service';
import { CreateAgentCommand, AgentResult } from '@generated/models';
import { NotificationService } from '@core/services/notification.service';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { NEW_AGENT_FIELDS } from '@core/assistant/assistant-catalog';

@Component({
  selector: 'app-agent-create',
  standalone: true,
  imports: [
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    AssistantActionDirective,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    ProgressSpinnerModule
],
  templateUrl: './agent-create.html',
  styleUrl: './agent-create.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AgentCreateComponent implements OnInit {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = NEW_AGENT_FIELDS;

  private fb = inject(FormBuilder);
  private agentsService = inject(AgentsService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private cdr = inject(ChangeDetectorRef);
  private notificationService = inject(NotificationService);

  createForm!: FormGroup;
  loading = signal(false);
  isDuplicating = signal(false);
  duplicateSourceId: string | null = null;
  maxNameLength = 50;

  readonly createTitle = $localize`:@@agentCreate.title.create:Complete your agent`;
  readonly duplicateTitle = $localize`:@@agentCreate.title.duplicate:Duplicate agent`;
  readonly createSubmitLabel = $localize`:@@agentCreate.submit.create:Create Agent`;
  readonly duplicateSubmitLabel = $localize`:@@agentCreate.submit.duplicate:Duplicate Agent`;
  get maxLengthError(): string {
    return $localize`:@@agentCreate.name.maxLength:Agent name cannot exceed ${this.maxNameLength}:max: characters`;
  }

  ngOnInit() {
    this.createForm = this.fb.group({
      title: ['', [Validators.required, Validators.maxLength(this.maxNameLength)]]
    });

    // Check if we're in duplicate mode
    const idParam = this.route.snapshot.paramMap.get('id');
    if (idParam) {
      this.duplicateSourceId = idParam;
      this.isDuplicating.set(true);
      this.loadSourceAgentTitle();
    }
  }

  get titleControl() {
    return this.createForm.get('title');
  }

  get titleLength(): number {
    return this.titleControl?.value?.length || 0;
  }

  goBack() {
    this.router.navigate(['/agents']);
  }

  private getBrowserLocale(): string {
    // Get browser locale (e.g., 'en-US', 'ru-RU')
    const browserLocale = navigator.language || 'en-US';
    return browserLocale;
  }

  private loadSourceAgentTitle() {
    if (!this.duplicateSourceId) return;

    this.loading.set(true);
    this.cdr.markForCheck();

    // Load source agent to get its title and pre-fill the form
    this.agentsService.apiAgentsIdGet({ id: this.duplicateSourceId }).subscribe({
      next: (agent: AgentResult) => {
        const copySuffix = $localize`:@@agentCreate.copySuffix:Copy`;
        const newTitle = `${agent.title} (${copySuffix})`;
        this.createForm.patchValue({ title: newTitle });
        this.loading.set(false);
        this.cdr.markForCheck();
      },
      error: (error: any) => {
        console.error('Error loading source agent:', error);
        this.notificationService.showError($localize`:@@agentCreate.loadSource.error:Failed to load source agent`);
        this.loading.set(false);
        this.cdr.markForCheck();
      }
    });
  }

  onSubmit() {
    if (this.createForm.valid && !this.loading()) {
      this.loading.set(true);

      if (this.isDuplicating()) {
        // Duplicate mode
        this.agentsService.apiAgentsDuplicatePost({
          body: {
            agent_id: this.duplicateSourceId!,
            new_title: this.titleControl?.value
          }
        }).subscribe({
          next: result => {
            console.log('Agent duplicated:', result);
            this.notificationService.showSuccess($localize`:@@agentCreate.duplicate.success:Agent duplicated successfully`);
            this.router.navigate(['/agents', result.id]);
          },
          error: (error: any) => {
            console.error('Error duplicating agent:', error);
            this.notificationService.showError($localize`:@@agentCreate.duplicate.error:Failed to duplicate agent`);
            this.loading.set(false);
            this.cdr.markForCheck();
          }
        });
      } else {
        // Create mode
        const browserLocale = this.getBrowserLocale();
        console.log('Browser locale:', browserLocale);

        const agentData: CreateAgentCommand = {
          title: this.titleControl?.value,
          language_locale_code: browserLocale
        };

        this.agentsService.apiAgentsPost({ body: agentData }).subscribe({
          next: (result) => {
            console.log('Agent created:', result);
            this.router.navigate(['/agents', result.id]);
          },
          error: (error: any) => {
            console.error('Error creating agent:', error);
            this.loading.set(false);
            this.cdr.markForCheck();
          }
        });
      }
    }
  }
}